import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useEngine, useWidgets } from '../api/context';
import { useMeta, useRemove, useResource, useUpdate } from '../api/hooks';
import { can, fieldOf, orderedFields, type FieldMeta, type ResourceMeta } from '../meta/types';
import { Button } from '../design/primitives/Button';
import { ErrorBanner, LifecycleRail, PageHeader, Skeleton, StatusBadge, type LifecycleStep, type Tone } from '../design/primitives/Misc';
import { useConfirm } from '../design/primitives/Overlay';
import { Tabs, type TabDef as UiTab } from '../design/primitives/Tabs';
import type { IconName } from '../design/icons';
import { FieldEditor, renderField, type Widgets } from '../fields/registry';
import { ActivityFeed } from '../views/ActivityFeed';
import { NotesPanel } from '../views/NotesPanel';
import { AttachmentsPanel } from '../views/AttachmentsPanel';
import { ActionButtons, resolveActions, useActionRunner } from './actions';
import { ResourceFormScreen, labelOf } from './ResourceFormScreen';
import { ResourceListScreen } from './ResourceListScreen';
import { useHotkeys, usePageActions } from '../layout/shortcuts';
import type { ScreenDef, TabDef } from './types';
import { getPath } from '../meta/path';

// ResourceDetailScreen: header (displayField + status + lifecycle rail) · primary actions
// from meta (confirm dialogs) · tabs: Overview (inline-edit key/values), Activity, Notes,
// Attachments, plus ScreenDef.tabs (related lists / custom components).

export interface ResourceDetailScreenProps {
  def: ScreenDef;
  /** Override the route param. */
  id?: string;
  /** Path of the list screen for the breadcrumb. */
  listPath?: string;
}

export function lifecycleSteps(meta: ResourceMeta, row: Record<string, unknown>): LifecycleStep[] | null {
  const lc = meta.lifecycle;
  if (!lc) return null;
  const f = fieldOf(meta, lc.field);
  const cur = String(row[lc.field] ?? '');
  const label = (v: string) => f?.enum?.find((o) => o.value === v)?.label ?? v.replace(/_/g, ' ');
  if (lc.terminal?.includes(cur)) {
    // A terminal state after the first step: everything before counts as done.
    return [...lc.steps.slice(0, 1).map((s) => ({ label: label(s), state: 'done' as const })), { label: label(cur), state: 'terminal' as const }];
  }
  const idx = lc.steps.indexOf(cur);
  return lc.steps.map((s, i) => ({ label: label(s), state: i < idx ? 'done' : i === idx ? 'current' : 'upcoming' }));
}

export function ResourceDetailScreen({ def, id: idProp, listPath }: ResourceDetailScreenProps) {
  const params = useParams();
  const id = idProp ?? params.id ?? '';
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { modules } = useEngine();
  const widgets = useWidgets();
  const confirm = useConfirm();
  const meta = useMeta(def.resource);
  const m = meta.data;
  const rowQ = useResource<Record<string, unknown>>(def.resource, id, { expand: def.query?.expand });
  const row = rowQ.data;
  const update = useUpdate(def.resource);
  const remove = useRemove(def.resource, { toast: { success: `${m?.label ?? 'Record'} deleted` }, onSuccess: () => navigate(listFor()) });
  const runner = useActionRunner(def.resource);
  const [editOpen, setEditOpen] = useState(false);

  const listFor = () => {
    if (listPath) return listPath;
    const parent = def.path.replace(/\/:id$/, '');
    const exists = modules.some((mod) => mod.screens.some((s) => s.path === parent));
    return exists ? parent : '/';
  };

  const tabs = useMemo<UiTab[]>(() => {
    const builtin = def.builtinTabs ?? ['overview', 'activity', 'notes', 'attachments'];
    const out: UiTab[] = [];
    if (builtin.includes('overview')) out.push({ key: 'overview', label: 'Overview', icon: 'file-text' });
    for (const t of def.tabs ?? []) if (!t.when || (row && t.when(row))) out.push({ key: t.key, label: t.label, icon: t.icon });
    if (builtin.includes('activity')) out.push({ key: 'activity', label: 'Activity', icon: 'activity' });
    if (builtin.includes('notes')) out.push({ key: 'notes', label: 'Notes', icon: 'sticky-note' });
    if (builtin.includes('attachments')) out.push({ key: 'attachments', label: 'Files', icon: 'paperclip' });
    return out;
  }, [def.builtinTabs, def.tabs, row]);
  const tab = sp.get('tab') && tabs.some((t) => t.key === sp.get('tab')) ? sp.get('tab')! : (tabs[0]?.key ?? 'overview');
  const setTab = (k: string) => {
    const next = new URLSearchParams(sp);
    if (k === tabs[0]?.key) next.delete('tab');
    else next.set('tab', k);
    setSp(next, { replace: true });
  };

  // The record's actions, registered once (usePageActions): e edit, and every item action the row
  // allows — by key, from ⌘K (`>`), the `.` quick menu and the `?` sheet.
  const pageItems = m && row ? resolveActions(m, def.actions, 'item', row).filter((a) => !a.component) : [];
  usePageActions(
    !m || !row
      ? []
      : [
          ...(m.capabilities.update && can(m, 'update') ? [{ id: 'edit', label: `Edit ${m.label.toLowerCase()}`, keys: 'e', icon: 'pencil' as IconName, group: 'Record', run: () => setEditOpen(true) }] : []),
          ...pageItems.map((a) => ({ id: `action:${a.name}`, label: a.label, icon: a.icon as IconName | undefined, group: 'Record', run: () => runner.run(a, { id, row, refresh: () => void rowQ.refetch() }) })),
        ],
  );
  useHotkeys([
    { keys: 'esc', run: () => navigate(listFor()), when: () => !editOpen },
    { keys: '[', run: () => setTab(tabs[Math.max(0, tabs.findIndex((t) => t.key === tab) - 1)]?.key ?? tab), label: 'Previous tab', group: 'Record' },
    { keys: ']', run: () => setTab(tabs[Math.min(tabs.length - 1, tabs.findIndex((t) => t.key === tab) + 1)]?.key ?? tab), label: 'Next tab', group: 'Record' },
  ]);

  if (meta.error) return <ErrorBanner error={meta.error} />;
  if (rowQ.error) return <ErrorBanner error={rowQ.error} action={<Button size="sm" variant="ghost" onClick={() => navigate(listFor())}>Back</Button>} />;
  if (!m || !row) {
    return (
      <div className="hf-page">
        <Skeleton height={28} width={240} />
        <div style={{ marginTop: 16 }}>
          <Skeleton lines={6} />
        </div>
      </div>
    );
  }

  const title = labelOf(m, row);
  const statusField = m.lifecycle ? fieldOf(m, m.lifecycle.field) : m.fields.find((f) => f.name === 'status');
  const statusVal = statusField ? row[statusField.name] : undefined;
  const steps = lifecycleSteps(m, row);
  const actions = resolveActions(m, def.actions, 'item', row);
  const entityType = m.table;
  const canUpdate = m.capabilities.update && can(m, 'update');
  const canRemove = m.capabilities.remove && can(m, 'delete');

  const refresh = () => rowQ.refetch();

  const panel = (key: string): ReactNode => {
    if (key === 'overview') return <Overview meta={m} row={row} widgets={widgets} editable={canUpdate} onEdit={(f, v, prev) => update.mutateAsync({ id, body: { [f.name]: v }, previous: { [f.name]: prev } })} />;
    if (key === 'activity') return <ActivityFeed entityType={entityType} entityId={id} />;
    if (key === 'notes') return <NotesPanel entityType={entityType} entityId={id} />;
    if (key === 'attachments') return <AttachmentsPanel entityType={entityType} entityId={id} />;
    const t = def.tabs?.find((x) => x.key === key);
    if (!t) return null;
    if (t.component) return <t.component resource={def.resource} id={id} row={row} refresh={refresh} />;
    // Keyed per tab: the embedded list's state (sort, filters) must not leak from one tab's
    // resource to the next for a render (contracts briefly requested with sites' sort=name → 400).
    if (t.resource) return <RelatedList key={t.key} tab={t} id={id} row={row} />;
    return null;
  };

  return (
    <div className="hf-page">
      <PageHeader
        compact
        breadcrumb={[{ label: m.labelPlural, to: listFor() }, { label: title }]}
        title={title}
        badge={statusField && statusVal != null ? <StatusBadge status={String(statusVal)} tones={Object.fromEntries((statusField.enum ?? []).filter((o) => o.tone).map((o) => [o.value, o.tone as Tone]))} /> : undefined}
        subtitle={def.subtitle}
        actions={
          <>
            <ActionButtons actions={actions} onRun={runner.run} ctx={{ id, row, refresh }} max={2} extra={[
              ...(canUpdate ? [{ label: 'Edit', icon: 'pencil' as IconName, hint: 'E', onSelect: () => setEditOpen(true) }] : []),
              ...(canRemove
                ? [
                    { separator: true, label: '' },
                    {
                      label: 'Delete',
                      icon: 'trash' as IconName,
                      danger: true,
                      onSelect: async () => {
                        if ((await confirm({ title: `Delete ${title}?`, body: 'This cannot be undone.', confirmLabel: 'Delete', danger: true })) !== false) remove.mutate(id);
                      },
                    },
                  ]
                : []),
            ]} />
            {canUpdate && (
              <Button icon="pencil" onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
          </>
        }
      />
      {steps && <LifecycleRail steps={steps} />}
      {def.header && <def.header resource={def.resource} id={id} row={row} refresh={refresh} />}
      <Tabs tabs={tabs} value={tab} onChange={setTab} aria-label="Record sections" />
      <div className="hf-tabpanel" role="tabpanel">
        {panel(tab)}
      </div>
      {runner.dialogs}
      {editOpen && <ResourceFormScreen resource={def.resource} id={id} form={def.form} onClose={() => setEditOpen(false)} onDone={() => { setEditOpen(false); refresh(); }} />}
    </div>
  );
}

/** Key/value overview with click-to-edit on updatable fields; sections by `group`. */
export function Overview({ meta, row, widgets, editable, onEdit }: { meta: ResourceMeta; row: Record<string, unknown>; widgets: Widgets; editable: boolean; onEdit: (f: FieldMeta, value: unknown, previous: unknown) => Promise<unknown> }) {
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState<unknown>(undefined);
  const fields = orderedFields(meta).filter((f) => f.inDetail);
  const groups = new Map<string, FieldMeta[]>();
  for (const f of fields) {
    const g = f.group ?? '';
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(f);
  }
  const commit = async (f: FieldMeta) => {
    const v = draft;
    setEditing(null);
    if (v !== undefined && v !== getPath(row, f.name)) await onEdit(f, v, getPath(row, f.name));
  };
  return (
    <div className="hf-overview">
      {[...groups].map(([g, fs]) => (
        <section key={g || '_'}>
          {g && <div className="hf-caps" style={{ margin: '16px 0 4px' }}>{g}</div>}
          <dl className="hf-kv">
            {fs.map((f) => {
              const canEditField = editable && f.inUpdate && !f.readOnly;
              const isEditing = editing === f.name;
              return (
                <div key={f.name}>
                  <dt>{f.label}</dt>
                  <dd
                    className={canEditField ? 'editable' : undefined}
                    tabIndex={canEditField && !isEditing ? 0 : undefined}
                    role={canEditField && !isEditing ? 'button' : undefined}
                    aria-label={canEditField && !isEditing ? `Edit ${f.label}` : undefined}
                    onClick={() => {
                      if (canEditField && !isEditing) {
                        setDraft(getPath(row, f.name));
                        setEditing(f.name);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (canEditField && !isEditing && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        setDraft(getPath(row, f.name));
                        setEditing(f.name);
                      }
                    }}
                  >
                    {isEditing ? (
                      <div className="hf-row" style={{ alignItems: 'flex-start' }}>
                        <div className="hf-grow">
                          <FieldEditor field={f} value={draft} onChange={setDraft} row={row} autoFocus size="sm" widgets={widgets} onCommit={() => void commit(f)} onCancel={() => setEditing(null)} aria-label={`Edit ${f.label}`} />
                        </div>
                        <Button size="sm" variant="primary" icon="check" aria-label="Save" onClick={() => void commit(f)} />
                        <Button size="sm" variant="ghost" icon="x" aria-label="Cancel" onClick={() => setEditing(null)} />
                      </div>
                    ) : (
                      renderField(f, getPath(row, f.name), row, { widgets })
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}
    </div>
  );
}

/** `:id` → the record id; `:field` → the record's field value (e.g. ':billing_party_id'). */
function RelatedList({ tab, id, row }: { tab: TabDef; id: string; row: Record<string, unknown> }) {
  const resolve = (value: string | number | boolean) => (typeof value === 'string' && value.startsWith(':') ? (value === ':id' ? id : (row[value.slice(1)] as string | number | boolean | undefined) ?? null) : value);
  const lockedFilters = Object.entries(tab.filter ?? {}).map(([field, value]) => ({ field, op: 'eq' as const, value: resolve(value) }));
  const initial: Record<string, unknown> = {};
  for (const f of lockedFilters) initial[f.field] = f.value;
  const def: ScreenDef = { path: '', resource: tab.resource!, kind: 'list', columns: tab.columns, detailPath: null, inspector: true };
  return <ResourceListScreen def={def} embedded lockedFilters={lockedFilters} createInitial={initial} />;
}
